import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Feature} from 'geojson';
import {ConfigurationModel} from '../../../models/configuration.model';
import {WeatherWarningsLayerService} from '../../map-viewer/services/weather-warnings-layer.service';
import {LngLat} from 'maplibre-gl';
import moment from 'moment';
import SunCalc from 'suncalc';
import {MatDialog} from '@angular/material/dialog';
import {WeatherForecastComponent} from '../../weather-forecast/weather-forecast.component';
import {ToastService} from '../../../services/toast.service';
import {Subscription} from 'rxjs';
import {WeatherWarningComponent} from '../../weather-warning/weather-warning.component';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

interface WeatherDescriptionToIcon {
  shortTexts: string[];
  dayIcon: string;
  nightIcon: string;
}

export class GridInfo {
  cwa: string;
  gridX: number;
  gridY: number;
}

@Component({
  selector: 'app-weather-news',
  templateUrl: './weather-news.component.html',
  styleUrls: ['./weather-news.component.scss'],
  imports: [MatIconButton, MatIcon, MatTooltip],
})
export class WeatherNewsComponent implements OnInit, OnDestroy {
  @Input() configuration: ConfigurationModel;
  @Input() isCompact: boolean;

  debugLevel = false;
  lngLat: LngLat;
  warningsLabel: string;
  warningFeatures: Feature[];
  temperatureInCelsius: number;
  weatherIcon: string;
  stationName = '';
  gridInfo: GridInfo;

  // array of objects where each object contains a string list and an associated icons
  // https://fonts.google.com/icons?icon.query=weather
  descriptionToIconMappings: WeatherDescriptionToIcon[] = [
    {
      shortTexts: [
        'fair',
        'clear',
        'fair with haze',
        'clear with haze',
        'fair and breezy',
        'clear and breezy',
        'fair and windy',
        'windy',
        'breezy',
        'mostly clear',
      ],
      dayIcon: 'clear_day',
      nightIcon: 'clear_night',
    },
    {
      shortTexts: [
        'a few clouds',
        'a few clouds with haze',
        'a few clouds and breezy',
        'partly cloudy',
        'partly cloudy with haze',
        'partly cloudy and breezy',
        'partly cloudy and windy',
        'a few clouds and windy',
      ],
      dayIcon: 'partly_cloudy_day',
      nightIcon: 'partly_cloudy_night',
    },
    {
      shortTexts: [
        'mostly cloudy',
        'mostly cloudy with haze',
        'mostly cloudy and breezy',
        'overcast',
        'overcast with haze',
        'overcast and breezy',
        'overcast and windy',
        'mostly cloudy and windy',
        'cloudy',
        'cloudy and windy',
      ],
      dayIcon: 'cloud',
      nightIcon: 'cloud',
    },
    {
      shortTexts: [
        'fog/mist',
        'fog',
        'freezing fog',
        'shallow fog',
        'partial fog',
        'patches of fog',
        'fog in vicinity',
        'freezing fog in vicinity',
        'shallow fog in vicinity',
        'partial fog in vicinity',
        'patches of fog in vicinity',
        'showers in vicinity fog',
        'light freezing fog',
        'heavy freezing fog',
        'smoke',
        'haze',
      ],
      dayIcon: 'mist',
      nightIcon: 'mist',
    },
    {
      shortTexts: [
        'snow',
        'light snow',
        'heavy snow',
        'snow showers',
        'light snow showers',
        'heavy snow showers',
        'showers snow',
        'light showers snow',
        'heavy showers snow',
        'snow fog/mist',
        'light snow fog/mist',
        'heavy snow fog/mist',
        'snow showers fog/mist',
        'light snow showers fog/mist',
        'heavy snow showers fog/mist',
        'showers snow fog/mist',
        'light showers snow fog/mist',
        'heavy showers snow fog/mist',
        'snow fog',
        'light snow fog',
        'heavy snow fog',
        'snow showers fog',
        'light snow showers fog',
        'heavy snow showers fog',
        'showers snow fog',
        'light showers snow fog',
        'heavy showers snow fog',
        'showers in vicinity snow',
        'snow showers in vicinity',
        'snow showers in vicinity fog/mist',
        'snow showers in vicinity fog',
        'low drifting snow',
        'blowing snow',
        'snow low drifting snow',
        'snow blowing snow',
        'light snow low drifting snow',
        'light snow blowing snow',
        'light snow blowing snow fog/mist',
        'heavy snow low drifting snow',
        'heavy snow blowing snow',
        'thunderstorm snow',
        'light thunderstorm snow',
        'heavy thunderstorm snow',
        'snow grains',
        'light snow grains',
        'heavy snow grains',
        'heavy blowing snow',
        'blowing snow in vicinity',
        'heavy snow and fog',
      ],
      dayIcon: 'snowing',
      nightIcon: 'snowing',
    },
    {
      shortTexts: [],
      dayIcon: 'snowing_heavy',
      nightIcon: 'snowing_heavy',
    },
    {
      shortTexts: [
        'ice pellets',
        'light ice pellets',
        'heavy ice pellets',
        'ice pellets in vicinity',
        'showers ice pellets',
        'thunderstorm ice pellets',
        'ice crystals',
        'hail',
        'small hail/snow pellets',
        'light small hail/snow pellets',
        'heavy small hail/snow pellets',
        'showers hail',
        'hail showers',
        'rain ice pellets',
        'light rain ice pellets',
        'heavy rain ice pellets',
        'drizzle ice pellets',
        'light drizzle ice pellets',
        'heavy drizzle ice pellets',
        'ice pellets rain',
        'light ice pellets rain',
        'heavy ice pellets rain',
        'ice pellets drizzle',
        'light ice pellets drizzle',
        'heavy ice pellets drizzle',
      ],
      dayIcon: 'weather_hail',
      nightIcon: 'weather_hail',
    },
    {
      shortTexts: [
        'rain snow',
        'light rain snow',
        'heavy rain snow',
        'snow rain',
        'light snow rain',
        'heavy snow rain',
        'drizzle snow',
        'light drizzle snow',
        'heavy drizzle snow',
        'snow drizzle',
        'light snow drizzle',
        'heavy drizzle snow',
        'freezing rain',
        'freezing drizzle',
        'light freezing rain',
        'light freezing drizzle',
        'heavy freezing rain',
        'heavy freezing drizzle',
        'freezing rain in vicinity',
        'freezing drizzle in vicinity',
        'freezing rain rain',
        'light freezing rain rain',
        'heavy freezing rain rain',
        'rain freezing rain',
        'light rain freezing rain',
        'heavy rain freezing rain',
        'freezing drizzle rain',
        'light freezing drizzle rain',
        'heavy freezing drizzle rain',
        'rain freezing drizzle',
        'light rain freezing drizzle',
        'heavy rain freezing drizzle',
      ],
      dayIcon: 'weather_mix',
      nightIcon: 'weather_mix',
    },
    {
      shortTexts: ['funnel cloud', 'funnel cloud in vicinity', 'tornado/water spout'],
      dayIcon: 'tornado',
      nightIcon: 'tornado',
    },
    {
      shortTexts: [
        'thunderstorm',
        'thunderstorm rain',
        'light thunderstorm rain',
        'heavy thunderstorm rain',
        'thunderstorm rain fog/mist',
        'light thunderstorm rain fog/mist',
        'heavy thunderstorm rain fog and windy',
        'heavy thunderstorm rain fog/mist',
        'thunderstorm showers in vicinity',
        'light thunderstorm rain haze',
        'heavy thunderstorm rain haze',
        'thunderstorm fog',
        'light thunderstorm rain fog',
        'heavy thunderstorm rain fog',
        'thunderstorm light rain',
        'thunderstorm heavy rain',
        'thunderstorm rain fog/mist',
        'thunderstorm light rain fog/mist',
        'thunderstorm heavy rain fog/mist',
        'thunderstorm in vicinity fog/mist',
        'thunderstorm showers in vicinity',
        'thunderstorm in vicinity haze',
        'thunderstorm haze in vicinity',
        'thunderstorm light rain haze',
        'thunderstorm heavy rain haze',
        'thunderstorm fog',
        'thunderstorm light rain fog',
        'thunderstorm heavy rain fog',
        'thunderstorm hail',
        'light thunderstorm rain hail',
        'heavy thunderstorm rain hail',
        'thunderstorm rain hail fog/mist',
        'light thunderstorm rain hail fog/mist',
        'heavy thunderstorm rain hail fog/hail',
        'thunderstorm showers in vicinity hail',
        'light thunderstorm rain hail haze',
        'heavy thunderstorm rain hail haze',
        'thunderstorm hail fog',
        'light thunderstorm rain hail fog',
        'heavy thunderstorm rain hail fog',
        'thunderstorm light rain hail',
        'thunderstorm heavy rain hail',
        'thunderstorm rain hail fog/mist',
        'thunderstorm light rain hail fog/mist',
        'thunderstorm heavy rain hail fog/mist',
        'thunderstorm in vicinity hail',
        'thunderstorm in vicinity hail haze',
        'thunderstorm haze in vicinity hail',
        'thunderstorm light rain hail haze',
        'thunderstorm heavy rain hail haze',
        'thunderstorm hail fog',
        'thunderstorm light rain hail fog',
        'thunderstorm heavy rain hail fog',
        'thunderstorm small hail/snow pellets',
        'thunderstorm rain small hail/snow pellets',
        'light thunderstorm rain small hail/snow pellets',
        'heavy thunderstorm rain small hail/snow pellets',
        'thunderstorm in vicinity',
        'thunderstorm in vicinity fog',
        'thunderstorm in vicinity haze',
      ],
      dayIcon: 'thunderstorm',
      nightIcon: 'thunderstorm',
    },
    {
      shortTexts: [
        'rain showers',
        'light rain showers',
        'light rain and breezy',
        'heavy rain showers',
        'rain showers in vicinity',
        'light showers rain',
        'heavy showers rain',
        'showers rain',
        'showers rain in vicinity',
        'rain showers fog/mist',
        'light rain showers fog/mist',
        'heavy rain showers fog/mist',
        'rain showers in vicinity fog/mist',
        'light showers rain fog/mist',
        'heavy showers rain fog/mist',
        'showers rain fog/mist',
        'showers rain in vicinity fog/mist',
      ],
      dayIcon: 'rainy',
      nightIcon: 'rainy',
    },
    {
      shortTexts: [
        'light rain',
        'drizzle',
        'light drizzle',
        'heavy drizzle',
        'light rain fog/mist',
        'drizzle fog/mist',
        'light drizzle fog/mist',
        'heavy drizzle fog/mist',
        'light rain fog',
        'drizzle fog',
        'light drizzle fog',
        'heavy drizzle fog',
        'showers in vicinity',
        'showers in vicinity fog/mist',
        'showers in vicinity fog',
        'showers in vicinity haze',
      ],
      dayIcon: 'rainy_light',
      nightIcon: 'rainy_light',
    },
    {
      shortTexts: [
        'rain',
        'heavy rain',
        'rain fog/mist',
        'heavy rain fog/mist',
        'rain fog',
        'heavy rain fog       rain',
        'heavy rain',
        'rain fog/mist',
        'heavy rain fog/mist',
        'rain fog',
        'heavy rain fog',
      ],
      dayIcon: 'rainy_heavy',
      nightIcon: 'rainy_heavy',
    },
    {
      shortTexts: [
        'freezing rain snow',
        'light freezing rain snow',
        'heavy freezing rain snow',
        'freezing drizzle snow',
        'light freezing drizzle snow',
        'heavy freezing drizzle snow',
        'snow freezing rain',
        'light snow freezing rain',
        'heavy snow freezing rain',
        'snow freezing drizzle',
        'light snow freezing drizzle',
        'heavy snow freezing drizzle',
      ],
      dayIcon: 'rainy_snow',
      nightIcon: 'rainy_snow',
    },
    {
      shortTexts: [
        'dust',
        'low drifting dust',
        'blowing dust',
        'sand',
        'blowing sand',
        'low drifting sand',
        'dust/sand whirls',
        'dust/sand whirls in vicinity',
        'dust storm',
        'heavy dust storm',
        'dust storm in vicinity',
        'sand storm',
        'heavy sand storm',
        'sand storm in vicinity',
      ],
      dayIcon: 'airwave',
      nightIcon: 'airwave',
    },
  ];

  // a map to associate each collection with its associated string
  descriptionMappingsMap = new Map<string, WeatherDescriptionToIcon>();

  weatherLocationSubscription: Subscription;
  gridInfoSubscription: Subscription;

  static celsiusToFahrenheit = (degrees: number) => 1.8 * degrees + 32;

  constructor(
    private weatherWarningsService: WeatherWarningsLayerService,
    private dialog: MatDialog,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    // set the map
    this.descriptionToIconMappings.forEach(collection => {
      collection.shortTexts.forEach(item => {
        this.descriptionMappingsMap.set(item, collection);
      });
    });

    this.weatherLocationSubscription = this.weatherWarningsService.weatherLocation$.subscribe(lngLat => {
      if (!!lngLat) {
        this.lngLat = lngLat;
        this.loadBasedCoordinates();
      }
    });

    this.gridInfoSubscription = this.weatherWarningsService.gridInfo$.subscribe(gridInfo => {
      if (!!gridInfo) {
        this.gridInfo = gridInfo;
        this.loadBasedGridInfo();
      }
    });
  }

  ngOnDestroy() {
    this.weatherLocationSubscription?.unsubscribe();
    this.gridInfoSubscription?.unsubscribe();
  }

  loadBasedCoordinates() {
    this.weatherWarningsService
      .getWmsFeatures(this.lngLat)
      .then(featureCollection => {
        this.warningFeatures = featureCollection.features;
        const featureCount = featureCollection.features.length;
        if (featureCount === 0) {
          this.warningsLabel = null;
        } else if (featureCount === 1) {
          this.warningsLabel = featureCollection.features[0].properties.prod_type;
        } else {
          this.warningsLabel = `${featureCount} Weather Alerts`;
        }
      })
      .catch(error => {
        console.error(error);
        this.toast.long('Loading current Warnings/Alerts failed!');
      });
  }

  loadBasedGridInfo() {
    this.temperatureInCelsius = undefined;
    this.weatherWarningsService
      .getWeatherStations(this.gridInfo.cwa, this.gridInfo.gridX, this.gridInfo.gridY)
      .then(stationsResponse => {
        if (this.debugLevel) {
          console.log('Stations: ');
          console.log(stationsResponse);
        }
        if (stationsResponse.features.length === 0) {
          console.warn('No stations found!');
        }
        const firstStationFeature = stationsResponse.features[0];
        const firstStationProperties = firstStationFeature.properties;
        this.stationName = firstStationProperties.name;
        this.weatherWarningsService
          .getWeatherStationObservations(firstStationProperties.stationIdentifier)
          .then(observationsResponse => {
            if (this.debugLevel) {
              console.log('Observations: ');
              console.log(observationsResponse);
            }
            if (observationsResponse.features.length > 0) {
              const firstObservationProperties = observationsResponse.features[0].properties;
              // ignore old observations
              this.temperatureInCelsius = null;
              if (moment().diff(moment(firstObservationProperties.timestamp), 'hour') < 4) {
                if (firstObservationProperties.temperature.unitCode === 'wmoUnit:degC') {
                  this.temperatureInCelsius = firstObservationProperties.temperature.value;
                } else {
                  this.stationName = null;
                  console.warn(`Incorrect temperature units! ${firstObservationProperties.temperature.unitCode}`);
                }
              } else {
                this.stationName = null;
                console.warn(`Observation too old! Timestamp: ${firstObservationProperties.timestamp}`);
              }

              let searchString = null;
              // looking for the first valid text description among all observations
              for (const feature of observationsResponse.features) {
                if (feature.properties.textDescription.length > 0) {
                  searchString = feature.properties.textDescription.toLowerCase();
                  break;
                }
              }

              if (!!searchString) {
                if (this.descriptionMappingsMap.has(searchString)) {
                  const { shortTexts, dayIcon, nightIcon } = this.descriptionMappingsMap.get(searchString);
                  const isDaylight = this.isDaytimeAtCoordinates(new Date(), this.lngLat);
                  this.weatherIcon = isDaylight ? dayIcon : nightIcon;

                  if (this.debugLevel) {
                    console.log(`Found ${searchString} in the collection, day icon: ${dayIcon}, night icon: ${nightIcon}`);
                    console.log('The associated list is: ', shortTexts);
                  }
                } else {
                  // TODO maybe parse icon name
                  console.warn(`${searchString} not found in any collection.`);
                }
              } else {
                console.warn('No text description found!');
              }
            } else {
              // TODO try next station
              console.warn('No observations found for station ID: ' + firstStationProperties.stationIdentifier);
            }
          })
          .catch(error => {
            console.error(error);
            this.toast.long('Loading Weather Observations failed!');
          });
      })
      .catch(error => {
        console.error(error);
        this.toast.long('Loading Weather Stations failed!');
      });
  }

  getTemperature(): string {
    if (this.temperatureInCelsius === null) {
      return 'N/A';
    }
    if (this.configuration.useMetricSystem) {
      return String(this.temperatureInCelsius);
    } else {
      return WeatherNewsComponent.celsiusToFahrenheit(this.temperatureInCelsius).toFixed(0);
    }
  }

  onWeatherClick() {
    if (!!this.gridInfo) {
      const dialog = this.dialog.open(WeatherForecastComponent, {
        width: '700px',
        maxWidth: 700,
      });
      dialog.componentInstance.configuration = this.configuration;
    } else {
      console.warn('Grid Info not initialized!');
    }
  }

  onWarningsClick() {
    if (!!this.warningFeatures && this.warningFeatures.length > 0) {
      this.dialog.open(WeatherWarningComponent, {
        data: this.warningFeatures,
        width: '90%',
        maxWidth: 1000,
      });
    } else {
      console.warn('Warning features empty!');
    }
  }

  isDaytimeAtCoordinates(timestamp: Date, lngLat: LngLat): boolean {
    const datetime = moment(timestamp);
    const solarTimes = SunCalc.getTimes(timestamp, lngLat.lat, lngLat.lng);
    const nightEnd = solarTimes.nightEnd;
    const dayEnd = solarTimes.night;
    return datetime.isAfter(moment(nightEnd)) && datetime.isBefore(moment(dayEnd));
  }
}
